<template>
<Transition name="banner">
    <div v-if="displayBanner" class="maaln-banner-container">
        <div class="content-container">
            <NuxtImg
                src="/images/homepage/features/maaln/maaln-wordmark-oj.png"
                alt="My Arms Are Longer Now"
                class="logo"
            />
            <div class="wishlist-button">
                <WishlistButton
                    slug="my-arms-are-longer-now"
                    :wishlist-url="`https://store.steampowered.com/app/2646170/My_Arms_Are_Longer_Now/`"
                    :cta-color="wishlistColor"
                    :cta-hover-color="wishlistHoverColor"
                    :cta-dark="true"
                />
            </div>
        </div>
        <Transition name="close">
            <div
                v-if="showCloseButton"
                role="button"
                tabIndex="0"
                class="close-button"
                v-on:click="close"
                v-on:keydown.enter="close"
            >
                <NuxtImg
                    src="/images/icons/eye-close.svg"
                    alt="Ellipse"
                    class="eye-close"
                />
            </div>
        </Transition>
    </div>
</Transition>
<div v-if="displayBanner" class="gradient-bottom" />
</template>

<script setup lang="ts">
import WishlistButton from '../shopping/WishlistButton.vue'

const app = useNuxtApp()
const bannerVisible = ref(true)
const bannerClosed = ref(false)
const isMobile = ref(false)
const showCloseButton = ref(false)
const route = useRoute()
const isPackDetailPage = computed(() => route.name?.toString().startsWith('games-packs-packslug'))
const isExternalGamePage = computed(() => route.name?.toString().startsWith('games-publishing-externalgameslug'))
const isHomePage = computed(() => {
    const locale = app.$i18n.locale.value
    return route.path === '/' || route.path === `/${locale}`
})
const isLandingPage = computed(() => route.name?.toString().startsWith('page-landingpage'))
const emit = defineEmits(['visible'])

const wishlistColor = 'linear-gradient(180deg, #C7543B 0%, #D1593E 50%, #C25239 100%)'
const wishlistHoverColor = `
  radial-gradient(
    50% 50% at 50% 50%,
    rgba(255, 45, 0, 0.08) 0%,
    rgba(255, 45, 0, 0.04) 100%
  ),
  linear-gradient(
    180deg,
    #D0583E 0%,
    #E56145 50%,
    #D0583E 100%
  )
`

const displayBanner = computed(() => bannerVisible.value
    && !bannerClosed.value
    && isMobile.value
    && !isPackDetailPage.value
    && !isExternalGamePage.value
    && !isHomePage.value
    && !isLandingPage.value)

let closeButtonTimer: number | null
// emits event to hide promo banner if maaln banner visible
// handles timer handling for 10s close button delay
watch(displayBanner, (val) => {
    emit('visible', val)

    if (val) {
        showCloseButton.value = false

        // clear previous timer since user could've manually resized
        if (closeButtonTimer) clearTimeout(closeButtonTimer)

        // show close button after 10 seconds
        closeButtonTimer = window.setTimeout(() => {
            showCloseButton.value = true
        }, 10000)
    } else {
        showCloseButton.value = false
        if (closeButtonTimer) clearTimeout(closeButtonTimer)
    }
}, { immediate: true })

function updateIsMobile() {
    isMobile.value = window.innerWidth <= 850
}

function close() {
    bannerClosed.value = true
}

onMounted(() => {
    updateIsMobile()
    window.addEventListener('resize', updateIsMobile)
})

onUnmounted(() => {
    window.removeEventListener('resize', updateIsMobile)
})
</script>

<style lang="scss" scoped>
@use "$styles/kit.scss" as *;
.maaln-banner-container {
    position: fixed;
    display: flex;
    justify-content: center;
    bottom: 15px;
    z-index: 1000000;
    padding: 0 16px;
    border-radius: 12px;
    left: 15px;
    right: 15px;
    width: auto;
    height: 160px;

    background-image: url('/images/homepage/features/maaln/maaln-banner-bg.png');
    background-size: cover;
    background-position: bottom;
    background-repeat: no-repeat;

    @media only screen and (max-width: 440px) {
        height: 124px;
    }

    .close-button {
        position: absolute;
        top: -8px;
        right: -8px;
        cursor: pointer;

        .eye-close {
            width: 36px;
            height: 36px;
        }
    }

    .content-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 10px;
        padding-top: 16px;
        padding-bottom: 16px;
        margin: 0 auto;
        width: 100%;
        max-width: 360px;

        @media only screen and (max-width: 440px) {
            height: 124px;
            max-width: 280px;
        }

        .logo {
            width: 100%;
            max-width: 300px;

            @media only screen and (max-width: 440px) {
                max-width: 230px;
            }
        }

        .wishlist-button {
            width: 100%;
            max-width: 360px;
        }
    }
}

.gradient-bottom {
    position: fixed;
    bottom: 0;
    width: 100%;
    height: 160px;
    background: linear-gradient(0deg, #1A1A1A 0%, rgba(26, 26, 26, 0.80) 5%, rgba(26, 26, 26, 0.40) 60%, rgba(26, 26, 26, 0.00) 100%);
}

.banner-enter-active {
    transition: all 0.5s ease-out;
}

.banner-leave-active {
    transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}

.banner-enter-from,
.banner-leave-to {
    transform: translateY(100%);
    opacity: 0;
}

.banner-enter-to,
.banner-leave-from {
    transform: translateY(0);
    opacity: 1;
}

.close-enter-active {
    transition: all 0.5s ease-out;
}

.close-leave-active {
    transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}

.close-enter-from,
.close-leave-to {
  opacity: 0;
}

.close-enter-to,
.close-leave-from {
  opacity: 1;
}

</style>
